import { useMemo } from "react";
import { useGetNodesListQuery } from "../graphql/generated/types.gen";
import { useActiveOrganization } from "../hooks/useActiveOrganization";
import { scopeRecordNodeId } from "./describe";
import type { Rule } from "./types";

/**
 * Resolve the display names of the records that one-off (node/property) overrides
 * target, in a single batched query, so the rules list and editor can name the
 * specific record instead of showing an opaque "one specific record".
 */
export function useRecordNames(rules: Rule[]): Record<string, string> {
	const { activeOrganizationId } = useActiveOrganization();
	const ids = useMemo(
		() =>
			Array.from(
				new Set(
					rules
						.map((r) => scopeRecordNodeId(r.scope))
						.filter((id): id is string => id !== null),
				),
			),
		[rules],
	);

	const { data } = useGetNodesListQuery({
		variables: {
			organizationId: activeOrganizationId ?? "",
			first: ids.length,
			filter: { ids },
		},
		skip: !activeOrganizationId || ids.length === 0,
	});

	return useMemo(() => {
		const names: Record<string, string> = {};
		for (const edge of data?.nodes.edges ?? []) {
			names[edge.node.id] = edge.node.displayName;
		}
		return names;
	}, [data]);
}
